import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import {
  listNotifications,
  markNotifRead,
  markAllNotifsRead,
  getUnreadCount,
  registerFcmToken,
  getNotifPreferences,
  updateNotifPreferences,
} from "@/api/stnApi";
import { requestFcmToken, onForegroundMessage, getFirebaseConfig } from "@/lib/firebase";

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children, isAuthenticated }) => {
  const [notifications, setNotifications]   = useState([]);
  const [unreadCount,   setUnreadCount]      = useState(0);
  const [total,         setTotal]            = useState(0);
  const [loading,       setLoading]          = useState(false);
  const [preferences,   setPreferences]      = useState({ emailNotifications: true, pushNotifications: true });
  const fcmRegistered = useRef(false);

  // ── Fetch notifications from API ────────────────────────────────────────────

  const fetchNotifications = useCallback(async ({ page = 1, limit = 30, type, unread } = {}) => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      const data = await listNotifications({ page, limit, type, unread });
      if (page === 1) {
        setNotifications(data.notifications || []);
      } else {
        setNotifications(prev => [...prev, ...(data.notifications || [])]);
      }
      setUnreadCount(data.unreadCount ?? 0);
      setTotal(data.total ?? 0);
    } catch {
      // silently fail — don't break app if notifications fail
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // ── Initial load ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchNotifications();
    getNotifPreferences().then(p => setPreferences(p)).catch(() => {});
  }, [isAuthenticated, fetchNotifications]);

  // ── Firebase FCM registration (request permission once) ─────────────────────

  useEffect(() => {
    if (!isAuthenticated || fcmRegistered.current) return;
    fcmRegistered.current = true;

    requestFcmToken().then(token => {
      if (token) {
        registerFcmToken(token).catch(() => {});

        // Inject config into service worker for background push handling
        const firebaseConfig = getFirebaseConfig();
        if (firebaseConfig) {
          navigator.serviceWorker?.ready.then(reg => {
            reg.active?.postMessage({ type: "FIREBASE_CONFIG", config: firebaseConfig });
          }).catch(() => {});
        }
      }
    }).catch(() => {});
  }, [isAuthenticated]);

  // ── Foreground push messages ────────────────────────────────────────────────

  useEffect(() => {
    if (!isAuthenticated) return;
    let unsubscribe = () => {};

    onForegroundMessage((payload) => {
      const { title, body } = payload.notification || {};
      const data = payload.data || {};
      const newNotif = {
        _id:       data.notifId || String(Date.now()),
        type:      data.type || "system",
        title:     title || "New Notification",
        message:   body || "",
        link:      data.url || "",
        linkLabel: "View",
        read:      false,
        createdAt: new Date().toISOString(),
      };
      setNotifications(prev => [newNotif, ...prev]);
      setUnreadCount(c => c + 1);
      setTotal(t => t + 1);
    }).then(fn => { unsubscribe = fn; }).catch(() => {});

    return () => { unsubscribe(); };
  }, [isAuthenticated]);

  // ── Mark single read ────────────────────────────────────────────────────────

  const markRead = useCallback(async (id) => {
    setNotifications(prev =>
      prev.map(n => String(n._id) === String(id) ? { ...n, read: true } : n)
    );
    setUnreadCount(c => Math.max(0, c - 1));
    try {
      const r = await markNotifRead(id);
      if (r?.unreadCount !== undefined) setUnreadCount(r.unreadCount);
    } catch {}
  }, []);

  // ── Mark all read ───────────────────────────────────────────────────────────

  const markAllRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await markAllNotifsRead();
    } catch {}
  }, []);

  // ── Add notification locally (optimistic, from in-app events) ───────────────

  const addNotification = useCallback((n) => {
    const newNotif = {
      _id:       String(Date.now()),
      ...n,
      read:      false,
      createdAt: new Date().toISOString(),
    };
    setNotifications(prev => [newNotif, ...prev]);
    setUnreadCount(c => c + 1);
    setTotal(t => t + 1);
  }, []);

  // ── Preferences ─────────────────────────────────────────────────────────────

  const savePreferences = useCallback(async (prefs) => {
    setPreferences(p => ({ ...p, ...prefs }));
    await updateNotifPreferences(prefs);
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      total,
      loading,
      preferences,
      fetchNotifications,
      markRead,
      markAllRead,
      addNotification,
      savePreferences,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
